<template>
  <div class="main-content-container overflow-hidden">
    <PageTitle pageTitle="Payments" subTitle="Transactions" />

    <div class="card bg-white border-0 rounded-3 mb-4">
      <div class="card-body p-0">
        <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 p-4">
          <form class="position-relative table-src-form me-0" @submit.prevent>
            <input type="text" class="form-control" placeholder="Search payments..." v-model="searchQuery" />
            <i class="material-symbols-outlined position-absolute top-50 start-0 translate-middle-y">search</i>
          </form>
        </div>

        <div class="default-table-area style-two default-table-width">
          <div class="table-responsive">
            <table class="table align-middle">
              <thead>
                <tr>
                  <th scope="col">Payment ID</th>
                  <th scope="col">Order ID</th>
                  <th scope="col">Channel</th>
                  <th scope="col">Amount</th>
                  <th scope="col">Status</th>
                  <th scope="col">Proof</th>
                  <th scope="col">Verified By</th>
                  <th scope="col">Paid At</th>
                  <th scope="col">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="loading">
                  <td colspan="9" class="text-center py-4">Loading...</td>
                </tr>
                <tr v-else-if="filteredPayments.length === 0">
                  <td colspan="9" class="text-center py-4">No payments found.</td>
                </tr>
                <tr v-else v-for="payment in filteredPayments" :key="payment.payment_id">
                  <td>#{{ payment.payment_id }}</td>
                  <td><RouterLink :to="`/orders/${payment.order_id}`">Order #{{ payment.order_id }}</RouterLink></td>
                  <td>{{ payment.channel?.provider_name || 'N/A' }}</td>
                  <td class="text-body">Rp {{ Number(payment.order?.total_price || 0).toLocaleString('id-ID') }}</td>
                  <td>
                    <span class="badge bg-opacity-10 p-2 fs-12 fw-normal" :class="statusClass(payment.payment_status)">
                      {{ payment.payment_status }}
                    </span>
                  </td>
                  <td>
                    <span class="badge bg-opacity-10 p-2 fs-12 fw-normal" :class="proofClass(payment.proof_status)">
                      {{ payment.proof_status }}
                    </span>
                  </td>
                  <td class="text-body">{{ payment.proof?.verified_by?.name || '-' }}</td>
                  <td class="text-body">{{ payment.paid_at ? new Date(payment.paid_at).toLocaleDateString() : '-' }}</td>
                  <td>
                    <div class="d-flex align-items-center gap-1">
                      <RouterLink :to="`/payments/${payment.payment_id}`" class="ps-0 border-0 bg-transparent lh-1 position-relative top-2">
                        <i class="material-symbols-outlined fs-16 text-primary">visibility</i>
                      </RouterLink>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from 'vue';
import PageTitle from '@/components/Common/PageTitle.vue';
import paymentService from '@/services/paymentService';

export default defineComponent({
  name: 'PaymentListPage',
  components: {
    PageTitle
  },
  setup() {
    const payments = ref<any[]>([]);
    const loading = ref(true);
    const searchQuery = ref('');

    const fetchPayments = async () => {
      loading.value = true;
      try {
        const response = await paymentService.getAll();
        payments.value = response.data;
      } catch (error) {
        console.error('Error fetching payments:', error);
      } finally {
        loading.value = false;
      }
    };

    const statusClass = (status: string) => {
      switch (status) {
        case 'Pending': return 'bg-warning text-warning';
        case 'Paid': return 'bg-success text-success';
        case 'Failed': return 'bg-danger text-danger';
        default: return 'bg-secondary text-secondary';
      }
    };

    const proofClass = (status: string) => {
      switch (status) {
        case 'None': return 'bg-secondary text-secondary';
        case 'Uploaded': return 'bg-warning text-warning';
        case 'Verified': return 'bg-success text-success';
        case 'Rejected': return 'bg-danger text-danger';
        default: return 'bg-secondary text-secondary';
      }
    };

    const filteredPayments = computed(() => {
      let list = payments.value;
      if (searchQuery.value) {
        const q = searchQuery.value.toLowerCase();
        list = list.filter(p => 
          p.payment_id.toString().includes(q) || 
          p.order_id.toString().includes(q) ||
          (p.channel?.provider_name && p.channel.provider_name.toLowerCase().includes(q))
        );
      }
      return [...list].sort((a, b) => {
        const dateA = new Date(a.created_at || a.paid_at).getTime() || 0;
        const dateB = new Date(b.created_at || b.paid_at).getTime() || 0;
        if (dateA !== dateB) return dateB - dateA;
        return Number(b.payment_id || 0) - Number(a.payment_id || 0);
      });
    });

    onMounted(() => {
      fetchPayments();
    });

    return {
      payments,
      loading,
      searchQuery,
      filteredPayments,
      statusClass,
      proofClass
    };
  }
});
</script>
